import { useEffect, useState, useRef } from "react";
import api from "../lib/api";
import PageHeader, { PageBody, Card } from "../components/Shared";
import { toast } from "sonner";
import { FloppyDisk, Plus, Trash, ArrowUp, ArrowDown, UploadSimple, CircleNotch, Plug, CheckCircle, XCircle, DownloadSimple, ChartBar } from "@phosphor-icons/react";

const IMG_LABELS = {
  hero: "Hero (familia)", eco1: "Ecosistema · Bandera", eco2: "Ecosistema · 2",
  radio: "Radio", buenas: "Buenas Noticias", asi: "Así se Vivió",
  historias: "Historias", eventos: "Eventos", mercadito: "Mercadito", todo: "Todo en orden",
};
const SIZE_HINTS = {
  hero: "Sugerido: 1200×1000 px · horizontal · JPG/WebP",
  eco1: "Sugerido: 800×1000 px · vertical (bandera) · JPG/WebP",
  eco2: "Sugerido: 800×600 px · horizontal",
  radio: "Sugerido: 1200×800 px · horizontal (3:2)",
  buenas: "Sugerido: 1200×800 px · horizontal (3:2)",
  asi: "Sugerido: 1200×800 px · horizontal (3:2)",
  historias: "Sugerido: 1200×900 px · horizontal (4:3)",
  eventos: "Sugerido: 1200×800 px · horizontal (3:2)",
  mercadito: "Sugerido: 1200×800 px · horizontal (3:2)",
  todo: "Sugerido: 1000×700 px · horizontal (3:2)",
};
const MODULE_LABELS = {
  todo_en_orden: "Todo en orden", plan_de_regreso: "Plan de regreso",
  cuida_tu_bolsillo: "Cuida tu Bolsillo", laboral: "Laboral",
};
const field = "mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm";
const SOCIAL_LABELS = { facebook: "Facebook", instagram: "Instagram", tiktok: "TikTok", linkedin: "LinkedIn", youtube: "YouTube", x: "X (Twitter)" };
const txtLabel = (k) => k.replace(/^(home|asp)\./, "").replace(/_/g, " ");

function TextFields({ texts, prefix, onChange }) {
  const keys = Object.keys(texts).filter((k) => k.startsWith(prefix));
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {keys.map((k) => {
        const v = texts[k] ?? "";
        const long = v.length > 55 || v.includes("\n");
        return (
          <label key={k} className={`block ${long ? "md:col-span-2" : ""}`}>
            <span className="section-label">{txtLabel(k)}</span>
            {long
              ? <textarea value={v} onChange={(e) => onChange(k, e.target.value)} data-testid={`webcms-txt-${k}`} rows={2} className={`${field} resize-y`} />
              : <input value={v} onChange={(e) => onChange(k, e.target.value)} data-testid={`webcms-txt-${k}`} className={field} />}
          </label>
        );
      })}
    </div>
  );
}

function UploadButton({ onUploaded, accept = "image/*", testid }) {
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const handle = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/web/admin/upload", fd);
      onUploaded(data.url);
      toast.success("Archivo subido");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al subir");
    }
    setBusy(false);
    if (ref.current) ref.current.value = "";
  };
  return (
    <>
      <input ref={ref} type="file" accept={accept} onChange={handle} className="hidden" data-testid={testid} />
      <button type="button" onClick={() => ref.current?.click()} disabled={busy}
        className="mt-1 inline-flex items-center gap-1.5 text-xs font-medium bg-page border border-carbon-200 rounded px-2.5 py-2 whitespace-nowrap disabled:opacity-50">
        {busy ? <CircleNotch size={14} className="animate-spin" /> : <UploadSimple size={14} weight="bold" />}
        {busy ? "Subiendo…" : "Subir archivo"}
      </button>
    </>
  );
}

export default function WebCMS() {
  const [content, setContent] = useState(null);
  const [busy, setBusy] = useState(false);
  const [s3busy, setS3busy] = useState(false);
  const [s3status, setS3status] = useState(null);
  const [report, setReport] = useState(null);

  const loadReport = async () => {
    try {
      const { data } = await api.get("/web/admin/interest-report");
      setReport(data);
    } catch { /* noop */ }
  };

  const downloadCsv = async () => {
    try {
      const res = await api.get("/web/admin/interest-report.csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "interes-web.csv"; document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(url);
    } catch { toast.error("No se pudo descargar el CSV"); }
  };

  const checkS3 = async () => {
    setS3busy(true);
    try {
      const { data } = await api.get("/web/admin/s3-status");
      setS3status(data);
    } catch {
      setS3status({ ok: false, message: "No se pudo comprobar la conexión." });
    }
    setS3busy(false);
  };

  useEffect(() => {
    api.get("/web/admin/content").then(({ data }) => {
      setContent({ images: data.images || {}, modules: data.modules || {}, editorial: data.editorial || [], texts: data.texts || {}, social: data.social || {} });
    }).catch(() => toast.error("No se pudo cargar el contenido"));
    loadReport();
  }, []);

  if (!content) return <div className="p-10 text-carbon-500">Cargando…</div>;

  const setImg = (k, v) => setContent((c) => ({ ...c, images: { ...c.images, [k]: v } }));
  const setTxt = (k, v) => setContent((c) => ({ ...c, texts: { ...c.texts, [k]: v } }));
  const setSoc = (k, v) => setContent((c) => ({ ...c, social: { ...c.social, [k]: v } }));
  const setMod = (k, f, v) => setContent((c) => ({ ...c, modules: { ...c.modules, [k]: { ...c.modules[k], [f]: v } } }));
  const setEd = (i, f, v) => setContent((c) => {
    const ed = [...c.editorial]; ed[i] = { ...ed[i], [f]: v }; return { ...c, editorial: ed };
  });
  const moveEd = (i, d) => setContent((c) => {
    const ed = [...c.editorial]; const j = i + d; if (j < 0 || j >= ed.length) return c;
    [ed[i], ed[j]] = [ed[j], ed[i]]; return { ...c, editorial: ed.map((e, idx) => ({ ...e, order: idx + 1 })) };
  });
  const addEd = () => setContent((c) => ({
    ...c, editorial: [...c.editorial, {
      id: `ed-${Date.now()}`, title: "Nueva pieza", text: "", category: "",
      media_type: "image", media_url: "", cta_label: "Ver más", cta_module: "", cta_intent: "ver",
      cta_href: "", order: c.editorial.length + 1, visible: true,
    }],
  }));
  const delEd = (i) => setContent((c) => ({ ...c, editorial: c.editorial.filter((_, idx) => idx !== i) }));

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/web/admin/content", content);
      toast.success("Contenido guardado");
    } catch { toast.error("Error al guardar"); }
    setBusy(false);
  };

  return (
    <>
      <PageHeader
        label="Web Pública"
        title="Contenido del sitio (CMS)"
        description="Edita imágenes, módulos y el carrusel editorial. Solo contenido: no altera prompts, Journey ni reglas conversacionales."
      >
        <button onClick={save} disabled={busy} data-testid="webcms-save" className="inline-flex items-center gap-2 bg-carbon-900 text-white rounded px-4 py-2 text-sm font-medium disabled:opacity-50">
          <FloppyDisk size={16} weight="bold" /> {busy ? "Guardando…" : "Guardar cambios"}
        </button>
      </PageHeader>
      <PageBody>
        <div className="space-y-6 max-w-4xl">

          {/* S3 STATUS */}
          <Card className="p-6">
            <div className="section-label mb-2">Almacenamiento</div>
            <h2 className="font-heading text-xl mb-2">Bucket de Amazon S3</h2>
            <p className="text-sm text-carbon-500 mb-4">
              Las imágenes y videos subidos se guardan en tu bucket S3. Configura las credenciales en <code className="text-terracotta">backend/.env</code> (AWS_ACCESS_KEY_ID, AWS_SECRET_ACCESS_KEY, S3_BUCKET, S3_REGION) y comprueba la conexión.
            </p>
            <div className="flex items-center gap-3 flex-wrap">
              <button onClick={checkS3} disabled={s3busy} data-testid="webcms-test-s3" className="inline-flex items-center gap-2 bg-page border border-carbon-200 rounded px-4 py-2 text-sm font-medium disabled:opacity-50">
                {s3busy ? <CircleNotch size={15} className="animate-spin" /> : <Plug size={15} weight="bold" />} Comprobar conexión
              </button>
              {s3status && (
                <span data-testid="webcms-s3-result" className={`inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-full ${s3status.ok ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>
                  {s3status.ok ? <CheckCircle size={15} weight="fill" /> : <XCircle size={15} weight="fill" />} {s3status.message}
                </span>
              )}
            </div>
          </Card>

          {/* REPORTE DE INTERÉS WEB */}
          <Card className="p-6" data-testid="webcms-interest-report">
            <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
              <div>
                <div className="section-label mb-2">Medición</div>
                <h2 className="font-heading text-xl mb-1 flex items-center gap-2"><ChartBar size={20} weight="bold" /> Intereses recibidos desde la Web</h2>
                <p className="text-sm text-carbon-500">Registro anónimo (módulo/país, fecha, origen Web). No es un CRM ni crea seguimientos.</p>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={loadReport} data-testid="webcms-report-refresh" className="inline-flex items-center gap-2 bg-page border border-carbon-200 rounded px-3 py-2 text-sm font-medium"><CircleNotch size={14} weight="bold" /> Actualizar</button>
                <button onClick={downloadCsv} data-testid="webcms-report-csv" className="inline-flex items-center gap-2 bg-carbon-900 text-white rounded px-4 py-2 text-sm font-medium"><DownloadSimple size={15} weight="bold" /> Descargar CSV</button>
              </div>
            </div>
            {!report ? (
              <div className="text-sm text-carbon-500">Cargando métricas…</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <div className="section-label mb-2">Intereses por módulo</div>
                  {report.modules?.length ? (
                    <table className="w-full text-sm">
                      <thead><tr className="text-left text-carbon-500"><th className="py-1 font-medium">Módulo</th><th className="py-1 font-medium text-right">Intereses</th><th className="py-1 font-medium text-right">Siguió a Luciana</th></tr></thead>
                      <tbody>
                        {report.modules.map((m) => (
                          <tr key={m.key} className="border-t border-carbon-200" data-testid={`report-mod-${m.key}`}>
                            <td className="py-1.5">{m.label}</td>
                            <td className="py-1.5 text-right font-semibold">{m.interests}</td>
                            <td className="py-1.5 text-right text-carbon-500">{m.continued_to_luciana}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : <div className="text-sm text-carbon-500">Sin registros aún.</div>}
                </div>
                <div>
                  <div className="section-label mb-2">Intereses confirmados por país</div>
                  {report.countries?.length ? (
                    <table className="w-full text-sm">
                      <thead><tr className="text-left text-carbon-500"><th className="py-1 font-medium">País</th><th className="py-1 font-medium text-right">Confirmados</th></tr></thead>
                      <tbody>
                        {report.countries.map((c) => (
                          <tr key={c.country} className="border-t border-carbon-200" data-testid={`report-country-${c.country}`}>
                            <td className="py-1.5">{c.country}</td>
                            <td className="py-1.5 text-right font-semibold">{c.interests}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : <div className="text-sm text-carbon-500">Sin registros aún.</div>}
                  <p className="text-xs text-carbon-500 mt-3">Continuaron con Luciana (multipaís): <b>{report.country_continued_to_luciana}</b></p>
                </div>
              </div>
            )}
          </Card>

          {/* IMÁGENES */}
          <Card className="p-6">
            <div className="section-label mb-2">Imágenes</div>
            <h2 className="font-heading text-xl mb-1">Imágenes del sitio</h2>
            <p className="text-sm text-carbon-500 mb-4">Formatos: JPG, PNG, WebP o GIF · máx. 15 MB. Puedes pegar una URL o subir un archivo.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {Object.keys(content.images).map((k) => (
                <div key={k} className="flex gap-3 items-start">
                  <img src={content.images[k]} alt="" className="w-16 h-16 rounded object-cover border border-carbon-200 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <span className="section-label">{IMG_LABELS[k] || k}</span>
                    <input value={content.images[k] || ""} onChange={(e) => setImg(k, e.target.value)} data-testid={`webcms-img-${k}`} placeholder="https://…" className={field} />
                    <div className="flex items-center justify-between gap-2 mt-1">
                      <span className="text-xs text-carbon-400">{SIZE_HINTS[k] || ""}</span>
                      <UploadButton testid={`webcms-upload-${k}`} onUploaded={(url) => setImg(k, url)} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* MÓDULOS */}
          <Card className="p-6">
            <div className="section-label mb-2">Ecosistema en construcción</div>
            <h2 className="font-heading text-xl mb-4">Módulos "Y esto apenas comienza"</h2>
            <div className="space-y-5">
              {Object.keys(content.modules).map((k) => {
                const m = content.modules[k];
                return (
                  <div key={k} className="border border-carbon-200 rounded-lg p-4" data-testid={`webcms-mod-${k}`}>
                    <div className="text-sm font-semibold mb-3">{MODULE_LABELS[k] || k}</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <label className="block"><span className="section-label">Título</span>
                        <input value={m.title || ""} onChange={(e) => setMod(k, "title", e.target.value)} className={field} /></label>
                      <label className="block"><span className="section-label">Estado</span>
                        <select value={m.state} onChange={(e) => setMod(k, "state", e.target.value)} className={field}>
                          <option value="EN_DESARROLLO">En desarrollo</option>
                          <option value="EN_PLANIFICACION">En planificación</option>
                        </select></label>
                    </div>
                    <label className="block mt-3"><span className="section-label">Descripción</span>
                      <textarea value={m.desc || ""} onChange={(e) => setMod(k, "desc", e.target.value)} rows={2} className={`${field} resize-y`} /></label>
                    <div className="flex items-center gap-4 mt-3">
                      <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={!!m.cta} onChange={(e) => setMod(k, "cta", e.target.checked)} /> Mostrar CTA
                      </label>
                      <label className="block flex-1"><span className="section-label">Texto del CTA</span>
                        <input value={m.cta_label || ""} onChange={(e) => setMod(k, "cta_label", e.target.value)} className={field} /></label>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>

          {/* EDITORIAL */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="section-label mb-2">Carrusel</div>
                <h2 className="font-heading text-xl">Piezas editoriales (4–6)</h2>
              </div>
              <button onClick={addEd} data-testid="webcms-add-editorial" className="inline-flex items-center gap-1.5 text-sm font-medium bg-page border border-carbon-200 rounded px-3 py-2">
                <Plus size={15} weight="bold" /> Añadir
              </button>
            </div>
            <div className="space-y-5">
              {content.editorial.map((e, i) => (
                <div key={e.id || i} className="border border-carbon-200 rounded-lg p-4" data-testid={`webcms-ed-${i}`}>
                  <div className="flex items-center justify-between mb-3">
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={e.visible !== false} onChange={(ev) => setEd(i, "visible", ev.target.checked)} /> Visible
                    </label>
                    <div className="flex items-center gap-1.5">
                      <button onClick={() => moveEd(i, -1)} className="p-1.5 rounded border border-carbon-200" title="Subir"><ArrowUp size={14} /></button>
                      <button onClick={() => moveEd(i, 1)} className="p-1.5 rounded border border-carbon-200" title="Bajar"><ArrowDown size={14} /></button>
                      <button onClick={() => delEd(i)} data-testid={`webcms-del-ed-${i}`} className="p-1.5 rounded border border-carbon-200 text-red-600" title="Eliminar"><Trash size={14} /></button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <label className="block"><span className="section-label">Título</span>
                      <input value={e.title || ""} onChange={(ev) => setEd(i, "title", ev.target.value)} className={field} /></label>
                    <label className="block"><span className="section-label">Categoría</span>
                      <input value={e.category || ""} onChange={(ev) => setEd(i, "category", ev.target.value)} className={field} /></label>
                  </div>
                  <label className="block mt-3"><span className="section-label">Texto</span>
                    <textarea value={e.text || ""} onChange={(ev) => setEd(i, "text", ev.target.value)} rows={2} className={`${field} resize-y`} /></label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                    <label className="block"><span className="section-label">Tipo de media</span>
                      <select value={e.media_type || "image"} onChange={(ev) => setEd(i, "media_type", ev.target.value)} className={field}>
                        <option value="image">Imagen</option>
                        <option value="video">Video</option>
                      </select></label>
                    <label className="block"><span className="section-label">URL de media</span>
                      <input value={e.media_url || ""} onChange={(ev) => setEd(i, "media_url", ev.target.value)} placeholder="https://…" className={field} />
                      <div className="flex items-center justify-between gap-2 mt-1">
                        <span className="text-xs text-carbon-400">Sugerido: 1200×900 px (4:3) · imagen o video MP4</span>
                        <UploadButton testid={`webcms-upload-ed-${i}`} accept="image/*,video/mp4" onUploaded={(url) => setEd(i, "media_url", url)} />
                      </div>
                    </label>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                    <label className="block"><span className="section-label">Texto del enlace</span>
                      <input value={e.cta_label || ""} onChange={(ev) => setEd(i, "cta_label", ev.target.value)} className={field} /></label>
                    <label className="block"><span className="section-label">URL del enlace (opcional)</span>
                      <input value={e.cta_href || ""} onChange={(ev) => setEd(i, "cta_href", ev.target.value)} placeholder="Vacío = abre WhatsApp" className={field} /></label>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* TEXTOS HOME */}
          <Card className="p-6">
            <div className="section-label mb-2">Textos</div>
            <h2 className="font-heading text-xl mb-4">Textos de la Home</h2>
            <TextFields texts={content.texts} prefix="home." onChange={setTxt} />
          </Card>

          {/* TEXTOS ASPIRANTES */}
          <Card className="p-6">
            <div className="section-label mb-2">Textos</div>
            <h2 className="font-heading text-xl mb-4">Textos de la página Aspirantes</h2>
            <TextFields texts={content.texts} prefix="asp." onChange={setTxt} />
          </Card>

          {/* REDES SOCIALES */}
          <Card className="p-6">
            <div className="section-label mb-2">Redes sociales</div>
            <h2 className="font-heading text-xl mb-1">Enlaces de redes</h2>
            <p className="text-sm text-carbon-500 mb-4">Pega la URL de cada red. Las que dejes vacías no se muestran en el sitio.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {["facebook", "instagram", "tiktok", "linkedin", "youtube", "x"].map((k) => (
                <label key={k} className="block">
                  <span className="section-label">{SOCIAL_LABELS[k]}</span>
                  <input value={content.social[k] || ""} onChange={(e) => setSoc(k, e.target.value)} data-testid={`webcms-social-${k}`} placeholder="https://…" className={field} />
                </label>
              ))}
            </div>
          </Card>

        </div>
      </PageBody>
    </>
  );
}
